/**
 * The procedure list, sectioned by category (breakdowns tab redesign, 2026-10).
 *
 * Four columns, the way Cleer reads a line: Procedure (name and code), Coverage
 * (grey when it matches its category's %, bold when it differs; a chip when the
 * carrier says more than a %), Frequency limitation (plain words; a shared or
 * group limit in purple, a condition of this line in amber), Eligibility (with
 * what ProviderConnect would pay). A category's row is its name only: its % and
 * dollar maximum are in Coverage by category, just above. Codes of one line with identical answers fold
 * into one row. A click opens what the carrier said about the line, and its
 * Edit buttons; the pencil beside each code edits that code's line in one
 * click (without opening the row). Disclaimers fold into one line at the bottom.
 */
import React, { useMemo, useState } from "react";
import { Button } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";

import { rowCode } from "../../../lib/procedureFamilies";
import { buildSheet, dollars } from "./sheetModel";
import "./breakdownSheet.css";

const NEXT_TONE = { now: "ok", date: "warn", wait: "warn", tooth: "grey" };

function CoveredCell({ line, sectionPct }) {
  const { pct, status } = line.covered;
  const pctText = pct != null ? `${pct}%` : "";
  const differs = pct != null && sectionPct != null && pct !== sectionPct;
  return (
    <>
      {status ? <span className={`bs-chip ${status.tone}`}>{status.label}</span> : null}
      {pctText && !status ? <span className={differs ? "bs-diff" : "bs-same"}>{pctText}</span> : null}
      {pctText && status ? <div className="bs-sub">{pctText}</div> : null}
      {!pctText && !status ? <span className="bs-same">—</span> : null}
      {differs && !status ? <div className="bs-sub">differs from the category</div> : null}
      {line.deductible ? <div className="bs-sub">{line.deductible}</div> : null}
    </>
  );
}

function HowOftenCell({ line }) {
  const h = line.howOften;
  const main = [h.main, h.ages].filter(Boolean).join(" · ");
  return (
    <>
      {main ? <span>{main}{h.basis ? <span className="bs-basis">{` · ${h.basis}`}</span> : null}</span> : <span className="bs-same">—</span>}
      {line.shared.map((s) => <div className="bs-shared" key={s}>{s}</div>)}
      {line.rules.slice(0, 2).map((r) => <div className="bs-rule" key={r} title={r}>{r}</div>)}
      {line.rules.length > 2 ? <div className="bs-rule">{`+${line.rules.length - 2} more (open the line)`}</div> : null}
    </>
  );
}

function NextCell({ line }) {
  const n = line.next;
  return (
    <>
      {n.kind === "none" ? <span className="bs-same">—</span> : <span className={`bs-chip ${NEXT_TONE[n.kind] || "grey"}`}>{n.label}</span>}
      {n.sub ? <div className="bs-sub">{n.sub}</div> : null}
      {line.pays != null ? <div className="bs-pays">{`Pays ${dollars(line.pays)}`}</div> : null}
    </>
  );
}

// Edits one code's line without opening or closing the row.
function EditPen({ row, code, onEdit }) {
  if (!row || !onEdit) return null;
  const stop = (e) => { e.stopPropagation(); };
  return (
    <button type="button" className="pe-q-pen bs-pen" aria-label={`Edit ${code}`} title={`Edit ${code}`}
      onClick={(e) => { stop(e); onEdit(row); }} onKeyDown={stop}>
      <EditOutlined />
    </button>
  );
}

const rowOf = (line, code) => line.rows.find((r) => rowCode(r) === code) || null;

export default function ProcedureSections({ rows, categories, sharedMaxGroups, policy, carrierName, onEdit, onAdd, canAdd = true, intake = false }) {
  const sheet = useMemo(() => buildSheet({ rows, categories, sharedMaxGroups, policy }),
    [rows, categories, sharedMaxGroups, policy]);
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((prev) => Object.assign({}, prev, { [key]: !prev[key] }));
  const onKey = (e, key) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(key); }
  };
  const who = carrierName || "The carrier";
  const empty = !sheet.sections.length;

  return (
    <>
      <section className="bs-proc" aria-label="Coverage by procedure codes" data-testid="breakdown-procedures">
        <div className="bs-proc-head">
          <span className="t">Coverage by procedure codes</span>
          <span className="right">
            {canAdd ? <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>Add procedure</Button> : null}
          </span>
        </div>
        {/* Said on (nearly) every line, so taken off each one — and shown here,
            where it can't be missed ("Not covered because your coverage ended"). */}
        {sheet.planNotes.length ? (
          <div className="bs-plan-said" data-testid="breakdown-plan-notes">
            {`${who} says of every procedure: `}{sheet.planNotes.join(" · ")}
          </div>
        ) : null}
        {empty ? (
          <div className="bs-empty">No procedures on this breakdown yet. Add one, or request a breakdown.</div>
        ) : (
          <table className="bs-table">
            <colgroup><col style={{ width: "34%" }} /><col style={{ width: "16%" }} /><col style={{ width: "32%" }} /><col style={{ width: "18%" }} /></colgroup>
            <thead><tr><th>Procedure</th><th>Coverage</th><th>Frequency limitation</th><th>Eligibility</th></tr></thead>
            <tbody>
              {sheet.sections.map((section) => (
                <React.Fragment key={section.cat}>
                  <tr className="bs-sec">
                    <td colSpan={4}>
                      {section.cat}
                      {section.notCovered ? <span className="bs-chip bad" style={{ marginLeft: 8 }}>Not covered</span> : null}
                    </td>
                  </tr>
                  {section.lines.map((line) => {
                    const isOpen = !!open[line.key];
                    return (
                      <React.Fragment key={line.key}>
                        <tr className={`bs-row${isOpen ? " open" : ""}`} tabIndex={0} aria-expanded={isOpen}
                          onClick={() => toggle(line.key)} onKeyDown={(e) => onKey(e, line.key)} data-code={line.code}>
                          <td>
                            <span className="bs-name">{line.name}</span>
                            {line.qualifier ? <span className="bs-q">{`, ${line.qualifier}`}</span> : null}
                            <span className="bs-code" title={line.description || undefined}>{line.code}</span>
                            <EditPen row={rowOf(line, line.code) || line.rows[0]} code={line.code} onEdit={onEdit} />
                            {line.also.length ? (
                              <div className="bs-also">
                                {"also "}
                                {line.also.map((code, i) => (
                                  <React.Fragment key={code}>
                                    {i ? ", " : ""}
                                    {code}
                                    <EditPen row={rowOf(line, code)} code={code} onEdit={onEdit} />
                                  </React.Fragment>
                                ))}
                                {" · same answer"}
                              </div>
                            ) : null}
                          </td>
                          <td><CoveredCell line={line} sectionPct={section.pct} /></td>
                          <td><HowOftenCell line={line} /></td>
                          <td><NextCell line={line} /></td>
                        </tr>
                        {isOpen ? (
                          <tr className="bs-exp">
                            <td colSpan={4}>
                              <div className="bs-said-title">{`What ${who} said`}</div>
                              <div className="bs-said">{line.said.length ? line.said.join("\n") : "Nothing beyond the figures above."}</div>
                              {line.description ? <div className="bs-sub">{`${line.code} in the office's code table: ${line.description}`}</div> : null}
                              <div className="bs-exp-acts">
                                {line.rows.map((row) => (
                                  <Button key={row.id} size="small" onClick={(e) => { e.stopPropagation(); onEdit(row); }}>
                                    {line.rows.length > 1 ? `Edit ${row.procedure_code_details && row.procedure_code_details.procedure_code}` : "Edit line"}
                                  </Button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <div className="bs-foot">
        {sheet.unavailable.length ? (
          <div className="bs-unav">
            {"Not available from the carrier: "}
            {sheet.unavailable.map((u, i) => (
              <React.Fragment key={u.row.id != null ? u.row.id : u.code}>
                {i ? ", " : ""}
                {/* Fill-in links are an FTC intake cue; offices read the list. */}
                {intake
                  ? <button type="button" className="bs-link" onClick={() => onEdit(u.row)} title="Fill it in by hand">{`${u.name} ${u.code}`}</button>
                  : `${u.name} ${u.code}`}
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {sheet.finePrint.length ? (
          <details>
            <summary>{`Carrier fine print: ${sheet.finePrint.length} sentence${sheet.finePrint.length === 1 ? "" : "s"}, on ${sheet.finePrintRows} line${sheet.finePrintRows === 1 ? "" : "s"}`}</summary>
            <div className="bs-said" style={{ marginTop: 6 }}>{sheet.finePrint.join("\n")}</div>
          </details>
        ) : null}
      </div>
    </>
  );
}
